/**
 * The reactive half: what a client does when a request comes back 401.
 *
 * One policy, three transports (axios instances, RTK Query base query, the legacy thunk
 * `apiRequest`): a 401 is NOT a logout signal until a refresh has been tried. Order:
 *
 *   1. Storage holds a different token than the request was sent with → another tab (or
 *      another caller in this tab) already rotated. Replay with the current token; send nothing.
 *   2. Otherwise `refreshSession()` once → `rotated` / `adopted` → replay.
 *   3. `dead` → `endSession('expired')`: the login page explains why.
 *   4. `transient` → give up on THIS request (reject as before); the session stays.
 *
 * `/Auth/*` is excluded throughout: a 401 from `loginwithotp` is a wrong code, from `logout`
 * an already-dead session, and from `extend-session` itself the thing we are handling.
 */

import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';

import { getUserData } from '@/constants/global';

import { endSession, refreshSession } from './refresh-session';

export type UnauthorizedDecision = 'replay' | 'ended' | 'unrecovered';

/** `/api/v1/Auth/...` on any base — matched on the path segment, case-insensitive. */
export const isAuthPath = (url: string | undefined) => /\/auth\//i.test(url ?? '');

export const recoverFromUnauthorized = async (sentToken: string | undefined): Promise<UnauthorizedDecision> => {
	const current = getUserData().token;
	if (current && sentToken && current !== sentToken) return 'replay';

	const outcome = await refreshSession(sentToken ?? current);
	if (outcome === 'rotated' || outcome === 'adopted') return 'replay';
	if (outcome === 'dead') {
		endSession('expired');
		return 'ended';
	}
	return 'unrecovered';
};

export const isUnauthorizedError = (error: unknown): error is AxiosError =>
	axios.isAxiosError(error) && error.response?.status === 401;

/** Rebuild a caller-supplied header bag with the token storage holds NOW (thunk replay). */
export const withCurrentBearer = <T extends Record<string, string | undefined>>(headers: T): T => {
	if (!('Authorization' in headers)) return headers;
	const token = getUserData().token;
	return { ...headers, Authorization: token ? `Bearer ${token}` : '' };
};

interface IReauthConfig extends InternalAxiosRequestConfig {
	/** The access token this request went out with — compared against storage on a 401. */
	__sentToken?: string;
	/** Set on the single replay so a second 401 is final. */
	__isRetry?: boolean;
}

/**
 * Wire an axios instance: attach the bearer on every request, stamp which token was sent,
 * and recover once from a 401. Replaces the old "401 → logout" branch.
 */
export const installReauth = (instance: AxiosInstance) => {
	instance.interceptors.request.use((config) => {
		const token = getUserData().token;
		if (token) config.headers.Authorization = `Bearer ${token}`;
		(config as IReauthConfig).__sentToken = token;
		return config;
	});

	instance.interceptors.response.use(
		(response) => response,
		async (error: AxiosError) => {
			const config = error.config as IReauthConfig | undefined;
			if (error.response?.status !== 401 || !config || config.__isRetry || isAuthPath(config.url)) {
				return Promise.reject(error);
			}
			const decision = await recoverFromUnauthorized(config.__sentToken);
			if (decision !== 'replay') return Promise.reject(error);
			return instance.request({ ...config, __isRetry: true } as IReauthConfig);
		}
	);
};
